import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

export type SystemConfigItem = {
  key: string;
  value: unknown;
};

export type LlmTagProvider = 'anthropic' | 'stub';

export type LlmTagProviderConfig = {
  provider: LlmTagProvider;
  model?: string;
};

export type OperationalSystemConfigGroup =
  | 'authentication'
  | 'healthMonitor'
  | 'rateLimitAlert'
  | 'notifications'
  | 'maintenance'
  | 'documentTagLlm'
  | 'search';

type OperationalSystemConfigBase = {
  key: string;
  group: OperationalSystemConfigGroup;
  isOverridden: boolean;
};

export type OperationalSystemConfigItem =
  | (OperationalSystemConfigBase & {
      valueKind: 'positiveInteger';
      unit: 'count' | 'devices' | 'milliseconds' | 'seconds' | 'days';
      constraints: { min?: number; max?: number; integer?: boolean };
      effectiveValue: number;
      overrideValue: number | null;
    })
  | (OperationalSystemConfigBase & {
      valueKind: 'nonNegativeInteger';
      unit: 'count' | 'devices' | 'milliseconds' | 'seconds' | 'days';
      constraints: { min?: number; max?: number; integer?: boolean };
      effectiveValue: number;
      overrideValue: number | null;
    })
  | (OperationalSystemConfigBase & {
      valueKind: 'unitInterval';
      unit: null;
      constraints: { min?: number; max?: number };
      effectiveValue: number;
      overrideValue: number | null;
    })
  | (OperationalSystemConfigBase & {
      valueKind: 'boolean';
      unit: null;
      constraints: Record<string, never>;
      effectiveValue: boolean;
      overrideValue: boolean | null;
    })
  | (OperationalSystemConfigBase & {
      valueKind: 'semver';
      unit: 'version';
      constraints: { pattern?: string };
      effectiveValue: string;
      overrideValue: string | null;
    })
  | (OperationalSystemConfigBase & {
      valueKind: 'llmTagProvider';
      unit: null;
      constraints: { providers?: LlmTagProvider[] };
      effectiveValue: LlmTagProviderConfig | null;
      overrideValue: LlmTagProviderConfig | null;
    });

type SystemConfigListResponse = { data: SystemConfigItem[] };
type SystemConfigUpdateResponse = { data: SystemConfigItem };
type OperationalSystemConfigListResponse = { data: OperationalSystemConfigItem[] };

export const fetchSystemConfigs = async (token: string): Promise<SystemConfigItem[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/admin/system-config`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch system configs: ${response.status}`);
  }

  return ((await response.json()) as SystemConfigListResponse).data;
};

export const fetchOperationalSystemConfigs = async (token: string): Promise<OperationalSystemConfigItem[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/admin/system-config/operations`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch operational system configs: ${response.status}`);
  }

  return ((await response.json()) as OperationalSystemConfigListResponse).data;
};

export const updateSystemConfig = async (token: string, key: string, value: unknown): Promise<SystemConfigItem> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/admin/system-config/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify({ value }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update system config: ${response.status}`);
  }

  return ((await response.json()) as SystemConfigUpdateResponse).data;
};

export const resetSystemConfig = async (token: string, key: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/admin/system-config/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to reset system config: ${response.status}`);
  }
};
